import { filter, find, get, has } from 'lodash';
import { CurrentSchoolYear } from '../../../../shared/constants/current-school-year.constant';
import { SubjectAreas } from '../../../../shared/constants/subject-areas.constant';
import { F_AND_P_LEVELS } from '../../../../shared/services/mad-lib-services/grouping-functions/assessments/assessments-groupings';
import { IListViewColumn } from '../../../../shared/typings/interfaces/list-view.interface';
import { IAcademicListStudent } from '../../../../store/effects/projections';
import { GRADIENT_TYPE } from './../../../../shared/constants/list-view/cell-gradient.constant';
import { COLUMN_DATA_TYPE } from './../../../../shared/constants/list-view/cell-type.constant';
import { IAcademicMadLibModel, IConstants, IGrade } from './academic-mad-lib.interface';

// TODO: clean up interface of `getValueAtPath` have it take two params, a student and an options object(JJ)
export const studentName: IListViewColumn = {
  human: 'Students',
  path: 'studentDetails.name.lastFirst',
  key: 'studentName',
  dataType: 'String',
  orderBy: 'asc',
  cellType: COLUMN_DATA_TYPE.SECTION_HEADER,
  cellConfig: {},
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const data = get(student, 'studentDetails.name.lastFirst');
    const studentId = get(student, 'studentId');
    const dependencies = { studentId };
    return { data, dependencies };
  },
};

/**
 * Creates tooltip based on the students assignments history.
 * @returns {string} the tooltipMsg string.
 * @returns {null} when there is no history.
 */
export function _buildTooltip(history, subj, path): string {
  let tooltip = null;
  if (history && history.length > 0) {
    const year = CurrentSchoolYear.WITH_SY_PREFIX;
    const name = 'iReady';
    const skill = 'Overall';
    const adminScores = {
      admin1: '-',
      admin2: '-',
      admin3: '-',
    };
    tooltip = '';
    let i;
    let data;
    for (i = 0; i < history.length; i++) {
      data = history[i];
      const isNameMatch = data.name === name;
      const isSkillMatch = data.skill === skill;
      const isSubjectMatch = data.subj === subj;
      const isSchoolYearMatch = data.year === year;
      if (isNameMatch && isSkillMatch && isSubjectMatch && isSchoolYearMatch) {
        adminScores['admin' + data.admin] = data[path];
      }
    }
    for (i = 0; i < 3; i++) {
      tooltip = tooltip + 'Admin ' + (i + 1) + ': ';
      tooltip = tooltip + adminScores['admin' + (i + 1)];
      if (i + 1 < 3) tooltip = tooltip + '<br />';
    }
  }
  return tooltip;
}

export const latestRelLvl: IListViewColumn = {
  human: 'Relative',
  path: 'latestRelLvl',
  key: 'iReady',
  dataType: 'String',
  orderBy: 'asc',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: {
    gradientRule: {
      type: GRADIENT_TYPE.ACADEMIC_IREADY,
    },
  },
  tooltip: "Student's most recent iReady Relative Placement Level",
  // this human name is based on the selected dimension 2 -- ELA/Math iReady
  isHumanNameDynamic: true,
  setDynamicHumanName: (madLibModel, constants?: IConstants) => {
    const { Dimension2 } = madLibModel;
    if (constants) {
      const admin = constants.iready.admin;
      const currentYear = CurrentSchoolYear.WITH_SY_PREFIX;
      const constantYear = constants.iready.schoolYear;
      let adminYear = '';
      if (constantYear !== currentYear) adminYear = `${constantYear} `;
      latestRelLvl.human = `${Dimension2.human} ${adminYear}Admin ${admin} Relative`;
    } else {
      latestRelLvl.human = `${Dimension2.human} Relative`;
    }
  },
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const iReadyMapping = { ELA_IREADY: 'ELA', MATH_IREADY: 'Math' };
    const dimension2SelectionKey = madLibModel.Dimension2.key;
    const summary = student.assmnts.summary;
    const assessmentSubject = iReadyMapping[dimension2SelectionKey];
    const schoolRiskGroup = has(student, `schoolRiskGroup${assessmentSubject}`)
      ? get(student, `schoolRiskGroup${assessmentSubject}`)
      : null;
    const assessmentFromSubject = find(summary, { subj: assessmentSubject, name: 'iReady' }) as any;
    let riskGroup = null;
    let latestRelLvl = null;
    if (assessmentFromSubject) {
      riskGroup = assessmentFromSubject.doeRiskGroup;
      latestRelLvl = get(assessmentFromSubject, 'latestRelLvl');
    }
    const tooltipMsg = this._buildTooltip(student.assmnts.history, assessmentSubject, 'relLvl');

    const dependencies = {
      doeRiskGroup: riskGroup,
      iReadySelection: dimension2SelectionKey,
      schoolRiskGroup,
    };

    const data = {
      latestRelLvl,
      tooltipMsg,
    };

    return { data, dependencies };
  },
};

export const latestScore: IListViewColumn = {
  human: 'Overall',
  path: 'latestScore',
  key: 'overall',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.IREADY,
  cellConfig: {
    gradientRule: {},
  },
  tooltip: "Student's most recent iReady Overall score",
  // this human name is based on the selected dimension 2 -- ELA/Math iReady
  isHumanNameDynamic: true,
  setDynamicHumanName: (madLibModel, constants?: IConstants) => {
    const { Dimension2 } = madLibModel;
    if (constants) {
      const admin = constants.iready.admin;
      const currentYear = CurrentSchoolYear.WITH_SY_PREFIX;
      const constantYear = constants.iready.schoolYear;
      let adminYear = '';
      if (constantYear !== currentYear) adminYear = `${constantYear} `;
      latestScore.human = `${Dimension2.human} ${adminYear}Admin ${admin} Overall`;
    } else {
      latestScore.human = `${Dimension2.human} Overall`;
    }
  },
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const iReadyMapping = { ELA_IREADY: 'ELA', MATH_IREADY: 'Math' };
    const dimension2SelectionKey = madLibModel.Dimension2.key;
    const summary = student.assmnts.summary;
    const assessmentSubject = iReadyMapping[dimension2SelectionKey];
    const assessmentFromSubject = find(summary, { subj: assessmentSubject, name: 'iReady' });
    const latestScore = assessmentFromSubject ? get(assessmentFromSubject, 'latestScore') : null;
    const tooltipMsg = this._buildTooltip(student.assmnts.history, assessmentSubject, 'score');

    const dependencies = { iReadySelection: dimension2SelectionKey };

    const data = {
      latestScore,
      tooltipMsg,
    };

    return { data, dependencies };
  },
};

export const pctGrowth: IListViewColumn = {
  human: 'Growth',
  path: 'pctGrowth',
  key: 'growth',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: {
    gradientRule: {
      type: GRADIENT_TYPE.ACADEMIC_GROWTH,
    },
  },
  tooltip:
    'Represents the percentage of expected annual scale score growth that each student achieved between ' +
    'administrations. Declines in scale scores are represented as 0% growth.',
  // this human name is based on the selected dimension 2 -- ELA/Math iReady
  isHumanNameDynamic: true,
  setDynamicHumanName: madLibModel => {
    const { Dimension2 } = madLibModel;
    pctGrowth.human = `${Dimension2.human} Growth`;
  },
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const iReadyMapping = { ELA_IREADY: 'ELA', MATH_IREADY: 'Math' };
    const dimesion2SelectionKey = madLibModel.Dimension2.key;
    const summary = student.assmnts.summary;
    const assessmentSubject = iReadyMapping[dimesion2SelectionKey];
    const assessmentFromSubject = find(summary, { subj: assessmentSubject, name: 'iReady' });
    const data = assessmentFromSubject ? get(assessmentFromSubject, 'pctGrowth') : null;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const supports: IListViewColumn = {
  human: 'Academic Supports',
  path: 'activeStudentSupports',
  key: 'supports',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const studentSupports: any[] = get(student, 'activeStudentSupports');
    const studentSupportsForCat = studentSupports.filter(
      studentSupport => studentSupport.support.category === listType,
    );
    const data = studentSupportsForCat.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const subjectSupports: IListViewColumn = {
  human: '[Subj] Supports',
  path: 'activeStudentSupports',
  key: 'subjectSupports',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  // this human name is based on the selected dimension 2 -- subject failure
  isHumanNameDynamic: true,
  setDynamicHumanName: madLibModel => {
    const { Dimension2 } = madLibModel;
    subjectSupports.human = `${Dimension2.subject} Supports`;
  },
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const selectedSubject = madLibModel.Dimension2.key;
    const subjectHumanSuport = SubjectAreas[selectedSubject].humanShort;
    const studentSupports: any[] = get(student, 'activeStudentSupports');
    const filteredStudentSupports = studentSupports.filter(
      studentSupport =>
        studentSupport.support.metaData &&
        studentSupport.support.category === listType &&
        studentSupport.support.metaData &&
        studentSupport.support.metaData.subject === subjectHumanSuport,
    );
    const data = filteredStudentSupports.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const needsSubjectSupport: IListViewColumn = {
  human: 'Needs [Subj] Support',
  path: 'activeStudentSupports',
  key: 'needsSubjectSupport',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  tooltip: 'Yes if student is currently failing a high priority course',
  // this human name is based on the selected dimension 2 -- subject failure
  isHumanNameDynamic: true,
  setDynamicHumanName: madLibModel => {
    const { Dimension2 } = madLibModel;
    needsSubjectSupport.human = `Needs ${Dimension2.subject} Support`;
  },
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    courseCode: string,
  ) => {
    const selectedSubject = madLibModel.Dimension2.key;
    const selectedSubjectHumanShort = SubjectAreas[selectedSubject].humanShort;
    const {
      currProgram: { grades },
      activeStudentSupports,
    } = student;
    const courseData: any = find(grades, { code: courseCode });
    const { priority } = courseData;

    const filteredStudentSupports = activeStudentSupports.filter(
      studentSupport =>
        studentSupport.support.category === listType &&
        // not all academic studentSupport have a metaData prop ?
        studentSupport.support.metaData &&
        studentSupport.support.metaData.subject === selectedSubjectHumanShort,
    );
    const needsSupport = !filteredStudentSupports.length && priority === 'high';
    const data = needsSupport ? 'Yes' : '-';
    const dependencies = {};
    return { data, dependencies };
  },
};

export const assigned: IListViewColumn = {
  human: 'Support Start Date',
  path: 'activeStudentSupports',
  key: 'assigned',
  dataType: 'Date',
  orderBy: 'asc',
  cellType: COLUMN_DATA_TYPE.DATE,
  cellConfig: {},
  tooltip: "Student's start date in the support",
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const studentSupports: any[] = get(student, 'activeStudentSupports');
    const studentSupportsForCat = studentSupports.filter(
      studentSupport => studentSupport.support.category === listType,
    );
    const data = studentSupportsForCat;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const flags: IListViewColumn = {
  human: 'Academic Flags',
  path: 'flags',
  key: 'flags',
  dataType: 'Array',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.FLAG,
  cellConfig: {},
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const data = get(student, 'flags');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const currentYtd: IListViewColumn = {
  human: 'Current YTD Attendance',
  path: 'att.ytd',
  key: 'ytd',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.PERCENTAGE,
  cellConfig: {},
  tooltip: 'Current year-to-date attendance as of previous school day',
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    const data = get(student, 'att.ytd') || 0;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const borderline: IListViewColumn = {
  human: 'Total Borderline',
  path: 'currProgram.countBorderline',
  key: 'borderLine',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  tooltip: 'Total courses borderline (65-74) across all subjs in most recent MP',
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    let data = get(student, 'currProgram.countBorderline');
    if (!data) data = 0;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const borderlineHp: IListViewColumn = {
  human: 'Total Borderline',
  path: 'currProgram.countBorderlineHP',
  key: 'borderLine',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  tooltip: 'Total courses borderline (65-74) across all subjs in most recent MP',
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    let data = get(student, 'currProgram.countBorderlineHP');
    if (!data) data = 0;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const failing: IListViewColumn = {
  human: 'Total Failing',
  path: 'currProgram.countFailing',
  key: 'failing',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: { gradientRule: { type: GRADIENT_TYPE.ACADEMIC_FAILING } },
  tooltip: 'Total courses failing (<65) across all subjs in most recent MP',
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    let data = get(student, 'currProgram.countFailing');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const failingHp: IListViewColumn = {
  human: 'Total Failing',
  path: 'currProgram.countFailingHP',
  key: 'failing',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: {
    gradientRule: {
      type: GRADIENT_TYPE.ACADEMIC_FAILING,
    },
  },
  tooltip: 'Total courses failing (<65) across all subjs in most recent MP',
  getValueAtPath: (student: IAcademicListStudent, madLibModel: IAcademicMadLibModel, listType: string) => {
    let data = get(student, 'currProgram.countFailingHP');
    if (!data) data = 0;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const course: IListViewColumn = {
  human: 'Course Section',
  path: null,
  key: 'course',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    courseCode: string,
  ) => {
    const {
      currProgram: { grades },
    } = student;
    const course: any = find(grades, ({ mostRecent, code, pf }) => {
      const isMostRecentGradeForCode = pf === 'F' && code === courseCode && mostRecent;
      return isMostRecentGradeForCode;
    });
    const data = course ? `${course.code}-${course.section}` : null;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const latestGrade: IListViewColumn = {
  human: 'Latest grade',
  path: null,
  key: 'latestGrade',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  tooltip: 'Most recent marking period grade',
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    courseCode: string,
  ) => {
    const {
      currProgram: { grades },
    } = student;
    const mostRecentGrade: IGrade = find(grades, (gradeObj: IGrade) => {
      const { mostRecent, code, pf } = gradeObj;
      const isMostRecentGradeForCode = pf === 'F' && code === courseCode && mostRecent;
      return isMostRecentGradeForCode;
    });
    const data = mostRecentGrade ? mostRecentGrade.str : null;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const priority: IListViewColumn = {
  human: 'Course Priority',
  path: null,
  key: 'priority',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  tooltip:
    'Importance of the course for graduation; High indicates student will need to earn more than one credit per term in the subj in future term(s) in order to graduate on time if he/she fails',
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    courseCode: string,
  ) => {
    const {
      currProgram: { grades },
    } = student;
    const mostRecentGrade: IGrade = find(grades, (gradeObj: IGrade) => {
      const { mostRecent, code, pf } = gradeObj;
      const isMostRecentGradeForCode = pf === 'F' && code === courseCode && mostRecent;
      return isMostRecentGradeForCode;
    });
    const data = mostRecentGrade ? mostRecentGrade.priority : null;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const elaStateExam: IListViewColumn = {
  name: 'State Exam',
  human: 'ELA PR',
  path: 'latestScore',
  key: 'elaStateExam',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.STATE_EXAM,
  cellConfig: {},
  isHumanNameDynamic: true,
  setDynamicHumanName: (madLibModel, constants?: IConstants) => {
    if (constants) {
      const stateExamYear = constants.stateExam.stateExamYear;
      elaStateExam.human = `ELA PR\n${stateExamYear}`;
    }
  },
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    code,
    isIreadySchool,
  ) => {
    const summary = student.assmnts.summary;
    const stateAssessmentForSubj = find(summary, { name: 'State Assessment', subj: 'ELA' }) as any;
    const latestScore = stateAssessmentForSubj ? get(stateAssessmentForSubj, 'latestScore') : null;
    const doeRiskGroup = stateAssessmentForSubj ? get(stateAssessmentForSubj, 'doeRiskGroup') : null;
    const schoolRiskGroup = has(student, `schoolRiskGroupELA`) ? get(student, `schoolRiskGroupELA`) : null;
    const dependencies = { doeRiskGroup, schoolRiskGroup };
    let latestAbsLvl;

    if (isIreadySchool) {
      const ireadyAssessment = find(summary, { name: 'iReady', subj: 'ELA' }) as any;
      latestAbsLvl = ireadyAssessment ? get(ireadyAssessment, 'latestAbsLvl') : '-';
    }

    const tooltipMsg = latestAbsLvl ? `Most recent ELA iReady overall placement<br /><br />${latestAbsLvl}` : null;

    const data = {
      latestScore,
      tooltipMsg,
    };

    return { data, dependencies };
  },
};

export const mathStateExam: IListViewColumn = {
  name: 'State Exam',
  human: 'Math PR',
  path: 'latestScore',
  key: 'mathStateExam',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.STATE_EXAM,
  cellConfig: {},
  isHumanNameDynamic: true,
  setDynamicHumanName: (madLibModel, constants?: IConstants) => {
    if (constants) {
      const stateExamYear = constants.stateExam.stateExamYear;
      mathStateExam.human = `Math PR\n${stateExamYear}`;
    }
  },
  getValueAtPath: (
    student: IAcademicListStudent,
    madLibModel: IAcademicMadLibModel,
    listType: string,
    code,
    isIreadySchool,
  ) => {
    const summary = student.assmnts.summary;
    const stateAssessmentForSubj = find(summary, { name: 'State Assessment', subj: 'Math' }) as any;
    const latestScore = stateAssessmentForSubj ? get(stateAssessmentForSubj, 'latestScore') : null;
    const doeRiskGroup = stateAssessmentForSubj ? get(stateAssessmentForSubj, 'doeRiskGroup') : null;
    const schoolRiskGroup = has(student, `schoolRiskGroupMath`) ? get(student, `schoolRiskGroupMath`) : null;
    const dependencies = { doeRiskGroup, schoolRiskGroup };
    let latestAbsLvl;

    if (isIreadySchool) {
      const ireadyAssessment = find(summary, { name: 'iReady', subj: 'Math' }) as any;
      latestAbsLvl = ireadyAssessment ? get(ireadyAssessment, 'latestAbsLvl') : '-';
    }

    const tooltipMsg = latestAbsLvl ? `Most recent Math iReady overall placement<br /><br />${latestAbsLvl}` : null;

    const data = {
      latestScore,
      tooltipMsg,
    };

    return { data, dependencies };
  },
};

// DIMENSION 2 DROPDOWN OPTIONS
export const iReadyColumns = {
  0: studentName,
  10: latestRelLvl,
  12: latestScore,
  20: pctGrowth,
  30: supports,
  40: flags,
};

export const stateExamColumns = {
  0: studentName,
  1: currentYtd,
  10: elaStateExam,
  20: mathStateExam,
  30: supports,
  40: flags,
};

export const courseGradeColumns = {
  0: studentName,
  1: currentYtd,
  10: borderline,
  20: failing,
  30: supports,
};

export const highPriorityCourseGradeColumns = {
  0: studentName,
  1: currentYtd,
  10: borderlineHp,
  20: failingHp,
  30: supports,
};

export const priorityColumns = {
  0: studentName,
  10: course,
  20: latestGrade,
  30: subjectSupports,
  40: failing,
  50: needsSubjectSupport,
};

export const subjectSupportColumns = {
  0: studentName,
  10: course,
  20: latestGrade,
  30: priority,
  40: failing,
  50: needsSubjectSupport,
};

export const supportColumns = { 30: assigned };

export const instructScore: IListViewColumn = {
  human: 'Instruct. Admin [#]',
  key: 'instructScore',
  dataType: 'Object',
  orderBy: 'asc',
  tooltip: 'Instructional score for Admin [#]',
  cellType: COLUMN_DATA_TYPE.F_P_SCORE,
  cellConfig: {},
  isHumanNameDynamic: true,
  setDynamicHumanName: (madlibModel, constants: IConstants) => {
    // 1. get the current admin lvl from school_assessments collection joined and calculated in the front nd to the student obj
    const currAdminLvl = `${constants.adminNumber}`;
    // 2. reset the human name to Instruct. Admin 1 (e.g.)
    instructScore.human = instructScore.human.replace(/\[.*\]/gi, currAdminLvl);
    instructScore.tooltip = instructScore.tooltip.replace(/\[.*\]/gi, currAdminLvl);
  },
  getValueAtPath: (student: IAcademicListStudent, admin) => {
    const { studentAssessments } = student;
    const filteredAssmnts = filter(studentAssessments, asmnt => {
      const {
        schoolAssessment: { assessmentNickName },
      } = asmnt;
      return assessmentNickName === 'F & P';
    });

    const latestAssmnt = find(filteredAssmnts, asmnt => asmnt.schoolAssessment.adminNumber === admin);
    const instScore = latestAssmnt ? latestAssmnt.items[1].value : null;
    const assessmentDate = latestAssmnt ? latestAssmnt.assessedOn : null;

    const dependencies = {};
    const data = {
      data: instScore ? instScore : '-',
      calculatedStatus: instScore,
      date: assessmentDate,
    };
    return { data, dependencies };
  },
};

export const relScore: IListViewColumn = {
  human: 'Relative Admin [#]',
  key: 'relScore',
  dataType: 'Object',
  orderBy: 'asc',
  tooltip: 'Relative level is calculated based on instructional score',
  cellType: COLUMN_DATA_TYPE.F_P_SCORE,
  cellConfig: {},
  isHumanNameDynamic: true,
  setDynamicHumanName: (madlibModel, constants: IConstants) => {
    const currAdminLvl = `${constants.adminNumber}`;
    relScore.human = relScore.human.replace(/\[.*\]/gi, currAdminLvl);
  },
  getValueAtPath: (student: IAcademicListStudent, admin) => {
    const { studentAssessments } = student;
    const filteredAssmnts = filter(studentAssessments, asmnt => {
      const {
        schoolAssessment: { assessmentNickName },
      } = asmnt;
      return assessmentNickName === 'F & P';
    });

    const latestAssmnt = find(filteredAssmnts, asmnt => asmnt.schoolAssessment.adminNumber === admin);
    const relativeScore = latestAssmnt ? latestAssmnt.relativeScore : null;
    const assessmentDate = latestAssmnt ? latestAssmnt.assessedOn : null;

    let parsedRelativeScore;
    if (relativeScore === undefined || relativeScore === null) parsedRelativeScore = 'Needs assessment';
    else if (relativeScore === -1) parsedRelativeScore = '1 below';
    else if (relativeScore === -2) parsedRelativeScore = '2 below';
    else if (relativeScore <= -3) parsedRelativeScore = '3+ below';
    else parsedRelativeScore = 'At or above level';
    const dependencies = {};
    const data = {
      data: parsedRelativeScore,
      calculatedStatus: relativeScore,
      date: assessmentDate,
    };
    return { data, dependencies };
  },
};

export const expectScore: IListViewColumn = {
  human: 'Expected Admin [#]',
  key: 'expectScore',
  dataType: 'Object',
  orderBy: 'asc',
  tooltip: 'Expected instructional level for Admin [#]',
  cellType: COLUMN_DATA_TYPE.F_P_SCORE,
  cellConfig: {},
  // this human name is based on the selected dimension 2 -- ELA/Math iReady
  isHumanNameDynamic: true,
  setDynamicHumanName: (madlibModel, constants: IConstants) => {
    const currAdminLvl = `${constants.adminNumber}`;
    expectScore.human = expectScore.human.replace(/\[.*\]/gi, currAdminLvl);
    expectScore.tooltip = expectScore.tooltip.replace(/\[.*\]/gi, currAdminLvl);
  },
  getValueAtPath: (student: IAcademicListStudent, admin) => {
    const { studentAssessments } = student;
    const filteredAssmnts = filter(studentAssessments, asmnt => {
      const {
        schoolAssessment: { assessmentNickName },
      } = asmnt;
      return assessmentNickName === 'F & P';
    });

    const latestAssmnt = find(filteredAssmnts, asmnt => asmnt.schoolAssessment.adminNumber === admin);
    const expectedScore = latestAssmnt ? latestAssmnt.expectedScore : null;

    const dependencies = {};
    const data = {
      data: expectedScore ? expectedScore : '-',
      calculatedStatus: expectedScore,
    };
    return { data, dependencies };
  },
};

export const readLvlGrowth: IListViewColumn = {
  human: 'reading level growth',
  key: 'readLvlGrowth',
  dataType: 'Object',
  orderBy: 'asc',
  tooltip: 'Reading level growth for Admin [#]',
  cellType: COLUMN_DATA_TYPE.READING_GROWTH,
  cellConfig: {
    // ..
  },
  isHumanNameDynamic: true,
  setDynamicHumanName: (madlibModel, constants: IConstants) => {
    const currAdminLvl = `${constants.adminNumber}`;
    readLvlGrowth.tooltip = readLvlGrowth.tooltip.replace(/\[.*\]/gi, currAdminLvl);
  },
  getValueAtPath: (student: IAcademicListStudent, admin) => {
    const { studentAssessments } = student;
    const filteredAssmnts = filter(studentAssessments, asmnt => {
      const {
        schoolAssessment: { assessmentNickName },
      } = asmnt;
      return assessmentNickName === 'F & P';
    });

    const latestAssmnt = find(filteredAssmnts, asmnt => asmnt.schoolAssessment.adminNumber === admin);
    const prevAdmin = find(filteredAssmnts, asmnt => asmnt.schoolAssessment.adminNumber === admin - 1);
    const instScore = latestAssmnt ? latestAssmnt.items[1].value : null;
    const lvlGrowth =
      prevAdmin && prevAdmin.items[1].value && instScore
        ? F_AND_P_LEVELS[instScore] - F_AND_P_LEVELS[prevAdmin.items[1].value]
        : -27;
    let readingLevelGrowth;
    let growthStyling;
    if (lvlGrowth < 0 && lvlGrowth > -27) {
      readingLevelGrowth = 'Declined';
      growthStyling = 'warning';
    } else if (lvlGrowth === 0) {
      readingLevelGrowth = 'No growth';
      growthStyling = 'warning';
    } else if (lvlGrowth === -27) readingLevelGrowth = '-';
    else readingLevelGrowth = `+${lvlGrowth}`;
    const dependencies = {};
    const data = {
      data: readingLevelGrowth,
      calculatedStatus: lvlGrowth,
    };
    const style = growthStyling;
    return { data, style, dependencies };
  },
};

export const stateExam: IListViewColumn = {
  human: 'State Exam [School Year]',
  key: 'stateExam',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  isHumanNameDynamic: true,
  setDynamicHumanName: madLibModel => {
    const { START, END } = CurrentSchoolYear;
    const prevStart = parseInt(START) - 1;
    const prevEnd = parseInt(END) - 1;
    const prevYear = `SY20${prevStart}-${prevEnd}`;
    stateExam.human = `State Exam\n${prevYear}`;
  },
  getValueAtPath: (student: IAcademicListStudent) => {
    const { assmnts } = student;
    const { START, END } = CurrentSchoolYear;
    const prevStart = parseInt(START) - 1;
    const prevEnd = parseInt(END) - 1;
    const prevYear = `SY20${prevStart}-${prevEnd}`;
    const priorStateELA = find(assmnts.history, asmnt => {
      const { name, year, subj } = asmnt;
      return name === 'State Assessment' && year === prevYear && subj === 'ELA';
    });
    const stateScore = priorStateELA ? priorStateELA.pr : '-';
    const data = stateScore;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const moreInfo: IListViewColumn = {
  human: 'more info',
  key: 'moreInfo',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.MORE_INFO,
  cellConfig: {},
  isHumanNameDynamic: false,
  getValueAtPath: (student: IAcademicListStudent) => {
    const { att, spedDetails, ellDetails } = student;
    const { isSped } = spedDetails;
    const { isEll } = ellDetails;
    const { riskGroup } = att;
    const hasRiskGroup = riskGroup === 'Severely chronically absent' || riskGroup === 'Chronically absent';
    const showMoreInfo = isSped || isEll || hasRiskGroup;
    let moreInfoTooltip = showMoreInfo ? '<div class="tooltip-wrapper">Student is...<br>' : '';
    if (isSped) moreInfoTooltip += '<span class="tooltip-pill">SPED</span>';
    if (isEll) moreInfoTooltip += '<span class="tooltip-pill">ELL</span>';
    if (hasRiskGroup) moreInfoTooltip += `<span class="tooltip-pill">${riskGroup}</span>`;
    if (showMoreInfo) moreInfoTooltip += '</div>';
    const dependencies = {};
    const data = showMoreInfo ? moreInfoTooltip : '-';
    return { data, dependencies };
  },
};

export const fpRelScoreColumns = {
  0: studentName,
  1: instructScore,
  10: readLvlGrowth,
  20: stateExam,
  30: flags,
  40: moreInfo,
};

export const fpInstructScoreColumns = {
  0: studentName,
  1: relScore,
  10: expectScore,
  20: readLvlGrowth,
  30: stateExam,
  40: flags,
  50: moreInfo,
};
